import React, { useCallback, useContext, useEffect, useState } from 'react';
import { Box, FormControl, Select, MenuItem, Typography, SelectChangeEvent, List, ListItem, ListItemText, Chip } from '@mui/material';
import { useTranslation } from 'react-i18next';
import useNswagClient from '../../../../hooks/api/useNswagClient';
import { StaleRetailerDto, UserFranchiseList } from '../../../../app/services/api/ofdClient';
import { UserContext } from '../../../../components/shared/useUser';

interface StaleRetailersProps {
  staleRetailers: StaleRetailerDto[];
}

/**
 * Displays a list of stale retailers with their days since last order
 * @param staleRetailers - Array of stale retailer data
 */
const StaleRetailers = ({ staleRetailers }: StaleRetailersProps) => {
  const { t } = useTranslation('common');

  if (!staleRetailers?.length) {
    return (
      <Box sx={{ p: 2, textAlign: 'center' }}>
        <Typography
          variant="textSM"
          color="text.secondary"
        >
          {t('No stale retailers found')}
        </Typography>
      </Box>
    );
  }

  /**
   * Determines chip color based on days since last order
   * @param days - Number of days since last order
   * @returns Color severity: 'error' for >180 days, 'warning' for >120 days, 'default' otherwise
   */
  const getChipColor = (days: number | undefined | null): 'error' | 'warning' | 'default' => {
    if (days === undefined || days === null || days > 180) return 'error';
    if (days > 120) return 'warning';
    return 'default';
  };

  /**
   * Formats the days display label
   * @param days - Number of days since last order
   * @returns Formatted label string
   */
  const getDaysLabel = (days: number | undefined | null): string => {
    if (days === undefined || days === null) return t('Never ordered');
    return `${days} ${t('days')}`;
  };

  return (
    <Box sx={{ height: 200, overflow: 'auto' }}>
      <List dense>
        {staleRetailers.map((retailer, idx) => (
          <ListItem
            key={retailer.customerId || idx}
            sx={{ borderBottom: '1px solid', borderColor: 'divider', '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' } }}
            secondaryAction={
              <Chip
                label={getDaysLabel(retailer.daysSinceLastOrder)}
                size="small"
                color={getChipColor(retailer.daysSinceLastOrder)}
              />
            }
          >
            <ListItemText
              primary={retailer.customerName || retailer.customerNumber || t('Unknown')}
              secondary={retailer.lastOrderDate ? `${t('Last order')}: ${new Date(retailer.lastOrderDate).toLocaleDateString()}` : t('No orders placed')}
            />
          </ListItem>
        ))}
      </List>
    </Box>
  );
};

const StaleRetailersWidget = () => {
  const { franchises } = useContext(UserContext);
  const { getDashboardStaleRetailers } = useNswagClient();
  const [staleRetailers, setStaleRetailers] = useState<StaleRetailerDto[]>([]);
  const [days, setDays] = useState<number>(90);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchStaleRetailers = async () => {
      setIsLoading(true);
      try {
        // Build user franchises payload with both names and IDs
        const userFranchises: UserFranchiseList = {
          franchiseNames: franchises
            .filter(f => f.franchiseName)
            .map(f => f.franchiseName as string),
          franchiseIds: franchises
            .filter(f => f.franchiseId)
            .map(f => f.franchiseId as number),
          otherRegions: false,
          currentFranchiseName: '',
          taskView: false,
        };

        const response = await getDashboardStaleRetailers(days, userFranchises);
        setStaleRetailers(response.staleRetailers || []);
      } catch (error) {
        setStaleRetailers([]);
      } finally {
        setIsLoading(false);
      }
    };
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    fetchStaleRetailers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [days, franchises]);

  const handleDaysChange = useCallback((event: SelectChangeEvent<number>) => {
    setDays(event.target.value as number);
  }, []);

  return (
    <Box sx={{ height: 260 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography color="text.secondary">
          No orders in:
        </Typography>
        <FormControl
          size="small"
          sx={{ minWidth: 120 }}>
          <Select
            value={days}
            onChange={handleDaysChange}
            displayEmpty
          >
            <MenuItem value={30}>30 days</MenuItem>
            <MenuItem value={60}>60 days</MenuItem>
            <MenuItem value={90}>90 days</MenuItem>
            <MenuItem value={120}>120 days</MenuItem>
            <MenuItem value={180}>180 days</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {isLoading ? (
        <Typography
          color="text.secondary"
          sx={{ textAlign: 'center', py: 4 }}>
          Loading...
        </Typography>
      ) : (
        <StaleRetailers staleRetailers={staleRetailers} />
      )}
    </Box>
  );
};

export default StaleRetailersWidget;
